/**
 * Выбор вкладки-лидера (CLIENT_WEB.md §6.7, §9.2 п. 5).
 *
 * Зачем: store `pending_ops` общий для всех вкладок одного профиля браузера.
 * Без лидера две вкладки отправили бы одну операцию одновременно — от дублей
 * спасает только `client_message_id`, но лишние запросы и гонки остаются.
 * Лидер также держит единственное WebSocket-соединение.
 *
 * Механизм: Web Locks API — вкладка бесконечно удерживает account-scoped
 * lock. Если API нет, вкладки выбирают одного лидера по
 * `(started_at, tab_id)` через BroadcastChannel. В обоих путях есть
 * heartbeat и lifecycle-handoff для freeze/bfcache (CLIENT_WEB §6.7).
 */

import type { Match, Message } from '@shared/api/types'

const LOCK_PREFIX = 'dvizh-app-leader'
const FALLBACK_LEASE_PREFIX = 'dvizh-app-leader-lease'
const HEARTBEAT_MS = 2_000
const LEADER_TIMEOUT_MS = 6_000
const FALLBACK_CONFIRM_MS = 100

export const TAB_ID = `tab-${Math.random().toString(36).slice(2, 10)}`

type LeadershipListener = (isLeader: boolean) => void

const listeners = new Set<LeadershipListener>()
let isLeader = false
let started = false
let release: (() => void) | null = null
let activeLockName: string | null = null
let activeUserId: string | null = null
let requestAbort: AbortController | null = null
let electionGeneration = 0
let electionStartedAt = 0
let lastLeaderHeartbeatAt = 0
let heartbeatTimer: ReturnType<typeof setInterval> | null = null
let watchdogTimer: ReturnType<typeof setInterval> | null = null
let fallbackTimer: ReturnType<typeof setInterval> | null = null
let fallbackConfirmTimer: ReturnType<typeof setTimeout> | null = null
let electionUnsubscribe: (() => void) | null = null
let fallbackMode = false
let leadershipExclusive = false
let suspendedUserId: string | null = null
let lifecycleListenersInstalled = false

interface FallbackCandidate {
  startedAt: number
  lastSeenAt: number
}

const fallbackCandidates = new Map<string, FallbackCandidate>()

function setLeader(value: boolean): void {
  if (isLeader === value) return
  isLeader = value
  if (value) startHeartbeat()
  else stopHeartbeat()
  for (const listener of listeners) {
    try {
      listener(value)
    } catch {
      /* подписчик не должен ломать выбор лидера */
    }
  }
}

function startHeartbeat(): void {
  stopHeartbeat()
  publishHeartbeat()
  heartbeatTimer = setInterval(publishHeartbeat, HEARTBEAT_MS)
}

function stopHeartbeat(): void {
  if (heartbeatTimer) clearInterval(heartbeatTimer)
  heartbeatTimer = null
}

function publishHeartbeat(): void {
  if (!isLeader || !activeUserId) return
  postTabMessage({
    type: 'leader_heartbeat',
    user_id: activeUserId,
    tab_id: TAB_ID,
    started_at: electionStartedAt,
    sent_at: Date.now(),
  })
}

function compareCandidates(
  left: readonly [string, FallbackCandidate],
  right: readonly [string, FallbackCandidate],
): number {
  return left[1].startedAt - right[1].startedAt || left[0].localeCompare(right[0])
}

function leaseKey(userId: string): string {
  return `${FALLBACK_LEASE_PREFIX}-${userId}`
}

function readFallbackLease(userId: string): { tabId: string; startedAt: number; expiresAt: number } | null {
  try {
    const raw = globalThis.localStorage?.getItem(leaseKey(userId))
    if (!raw) return null
    const parsed = JSON.parse(raw) as { tabId?: unknown; startedAt?: unknown; expiresAt?: unknown }
    if (
      typeof parsed.tabId !== 'string' ||
      typeof parsed.startedAt !== 'number' ||
      typeof parsed.expiresAt !== 'number' ||
      parsed.expiresAt <= Date.now()
    ) {
      globalThis.localStorage?.removeItem(leaseKey(userId))
      return null
    }
    return { tabId: parsed.tabId, startedAt: parsed.startedAt, expiresAt: parsed.expiresAt }
  } catch {
    return null
  }
}

function writeFallbackLease(userId: string): boolean {
  try {
    // Живую lease другой вкладки не перезаписываем: localStorage
    // не имеет CAS, а read/write/read иначе дал бы короткое dual-leader окно.
    const existing = readFallbackLease(userId)
    if (existing && existing.tabId !== TAB_ID) return false
    globalThis.localStorage?.setItem(
      leaseKey(userId),
      JSON.stringify({
        tabId: TAB_ID,
        startedAt: electionStartedAt,
        expiresAt: Date.now() + LEADER_TIMEOUT_MS,
      }),
    )
    return readFallbackLease(userId)?.tabId === TAB_ID
  } catch {
    // Без проверяемой lease BroadcastChannel не даёт атомарного
    // арбитра: две вкладки могут ещё не увидеть кандидата друг друга.
    // Такое лидерство не считаем подтверждённым и WS/outbox не запускаем.
    return false
  }
}

function scheduleFallbackConfirmation(): void {
  if (fallbackConfirmTimer) return
  fallbackConfirmTimer = setTimeout(() => {
    fallbackConfirmTimer = null
    evaluateFallbackLeadership()
  }, FALLBACK_CONFIRM_MS)
}

function clearOwnFallbackLease(userId: string): void {
  try {
    if (readFallbackLease(userId)?.tabId === TAB_ID) globalThis.localStorage?.removeItem(leaseKey(userId))
  } catch {
    /* storage может быть запрещён политикой браузера */
  }
}

function clearFallbackLease(userId: string): void {
  try {
    globalThis.localStorage?.removeItem(leaseKey(userId))
  } catch {
    /* storage может быть запрещён политикой браузера */
  }
}

function evaluateFallbackLeadership(): void {
  const userId = activeUserId
  if (!fallbackMode || !userId) return
  const cutoff = Date.now() - LEADER_TIMEOUT_MS
  for (const [tabId, candidate] of fallbackCandidates) {
    if (candidate.lastSeenAt <= cutoff) fallbackCandidates.delete(tabId)
  }

  const lease = readFallbackLease(userId)
  if (lease) {
    const known = fallbackCandidates.get(lease.tabId)
    fallbackCandidates.set(lease.tabId, {
      startedAt: lease.startedAt,
      lastSeenAt: Math.max(known?.lastSeenAt ?? 0, lease.expiresAt - LEADER_TIMEOUT_MS),
    })
    // Lease — атомарный арбитр между двумя одновременными claim.
    // Пара `(started_at, tab_id)` выбирает нового владельца только после TTL.
    const ownLease = lease.tabId === TAB_ID
    // Heartbeat заодно продлевает свою lease. Без этого она
    // истекала каждые 6 секунд, разрывая WS и queue-runner.
    const verified = ownLease && writeFallbackLease(userId)
    leadershipExclusive = verified
    setLeader(verified)
    return
  }

  const winner = [...fallbackCandidates.entries()].sort(compareCandidates)[0]?.[0]
  if (winner !== TAB_ID) {
    leadershipExclusive = false
    setLeader(false)
    return
  }
  // Не провозглашаем себя лидером в том же turn, где записали lease.
  // После короткого окна снова читаем storage: если два simultaneous
  // startup успели записаться, только последний владелец пройдёт проверку.
  leadershipExclusive = false
  setLeader(false)
  if (writeFallbackLease(userId)) scheduleFallbackConfirmation()
}

function publishFallbackCandidate(): void {
  if (!fallbackMode || !activeUserId) return
  const now = Date.now()
  fallbackCandidates.set(TAB_ID, { startedAt: electionStartedAt, lastSeenAt: now })
  postTabMessage({
    type: 'leader_candidate',
    user_id: activeUserId,
    tab_id: TAB_ID,
    started_at: electionStartedAt,
    sent_at: now,
  })
  evaluateFallbackLeadership()
}

function handleElectionMessage(message: TabMessage): void {
  if (!activeUserId || !('user_id' in message) || message.user_id !== activeUserId) return
  if (message.type === 'leader_heartbeat') {
    if (message.tab_id !== TAB_ID) lastLeaderHeartbeatAt = Date.now()
    if (fallbackMode) {
      fallbackCandidates.set(message.tab_id, {
        startedAt: message.started_at,
        lastSeenAt: Date.now(),
      })
      evaluateFallbackLeadership()
    }
    return
  }
  if (!fallbackMode) return
  if (message.type === 'leader_candidate') {
    fallbackCandidates.set(message.tab_id, {
      startedAt: message.started_at,
      lastSeenAt: Date.now(),
    })
    evaluateFallbackLeadership()
  } else if (message.type === 'leader_withdrawn') {
    fallbackCandidates.delete(message.tab_id)
    evaluateFallbackLeadership()
  }
}

function requestWebLock(lockName: string): void {
  const locks = navigator.locks as LockManager | undefined
  if (!locks?.request) return
  requestAbort?.abort()
  const generation = ++electionGeneration
  const controller = new AbortController()
  requestAbort = controller
  void locks
    .request(lockName, { mode: 'exclusive', signal: controller.signal }, () => {
      if (controller.signal.aborted || generation !== electionGeneration || activeLockName !== lockName) {
        return
      }
      setLeader(true)
      leadershipExclusive = true
      return new Promise<void>((resolve) => {
        release = () => {
          setLeader(false)
          resolve()
        }
      })
    })
    .catch((error: unknown) => {
      if (controller.signal.aborted || (error instanceof DOMException && error.name === 'AbortError')) return
      console.error('leader lock request failed', error)
    })
}

function startWatchdog(lockName: string): void {
  lastLeaderHeartbeatAt = Date.now()
  watchdogTimer = setInterval(() => {
    if (isLeader || activeLockName !== lockName) return
    if (Date.now() - lastLeaderHeartbeatAt <= LEADER_TIMEOUT_MS) return
    // Старая queued-заявка могла остаться за замороженным лидером.
    // Abort + новый request — нормативный takeover §6.7 п. 6a.
    lastLeaderHeartbeatAt = Date.now()
    requestWebLock(lockName)
  }, HEARTBEAT_MS)
}

function installLifecycleListeners(): void {
  if (lifecycleListenersInstalled) return
  lifecycleListenersInstalled = true

  const suspend = (): void => {
    const userId = activeUserId
    if (!userId) return
    stopElection(false)
    suspendedUserId = userId
  }
  const resume = (): void => {
    const userId = suspendedUserId
    if (!userId || (typeof document !== 'undefined' && document.visibilityState === 'hidden')) return
    suspendedUserId = null
    startLeaderElection(userId)
  }
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') suspend()
    else resume()
  })
  document.addEventListener('freeze', suspend)
  document.addEventListener('resume', resume)
  globalThis.addEventListener('pagehide', suspend)
  globalThis.addEventListener('pageshow', resume)
}

/** Начинает борьбу за лидерство. Идемпотентна. */
export function startLeaderElection(userId = 'anonymous'): void {
  const lockName = `${LOCK_PREFIX}-${userId}`
  if (started && activeLockName === lockName) return
  if (started) stopElection(true)
  installLifecycleListeners()
  if (typeof document !== 'undefined' && document.visibilityState === 'hidden') {
    suspendedUserId = userId
    return
  }
  started = true
  activeLockName = lockName
  activeUserId = userId
  suspendedUserId = null
  electionStartedAt = Date.now()
  electionUnsubscribe = onTabMessage(handleElectionMessage)

  const locks = navigator.locks as LockManager | undefined
  if (!locks?.request) {
    // Если нет и BroadcastChannel, спецификация разрешает собственное
    // соединение; 4429 переведёт его в polling fallback.
    if (!getChannel()) {
      leadershipExclusive = false
      setLeader(true)
      return
    }
    fallbackMode = true
    leadershipExclusive = false
    const lease = readFallbackLease(userId)
    if (lease) {
      fallbackCandidates.set(lease.tabId, {
        startedAt: lease.startedAt,
        lastSeenAt: lease.expiresAt - LEADER_TIMEOUT_MS,
      })
    }
    publishFallbackCandidate()
    fallbackTimer = setInterval(publishFallbackCandidate, HEARTBEAT_MS)
    return
  }
  requestWebLock(lockName)
  startWatchdog(lockName)
}

/**
 * Добровольно уступает лидерство (выход, закрытие вкладки).
 *
 * Снимает признак и в запасном пути без Web Locks: там `release` не задан,
 * и без явного сброса вкладка осталась бы «лидером» после выхода, продолжая
 * считать очередь своей.
 */
export function releaseLeadership(): void {
  stopElection(true)
}

/** Удаляет lease даже после cold crash, когда module-state уже пуст. */
export function clearLeadershipStateForUser(userId: string): void {
  clearFallbackLease(userId)
}

function stopElection(clearSuspendedUser: boolean): void {
  const userId = activeUserId
  electionGeneration += 1
  requestAbort?.abort()
  requestAbort = null
  release?.()
  release = null
  if (watchdogTimer) clearInterval(watchdogTimer)
  if (fallbackTimer) clearInterval(fallbackTimer)
  if (fallbackConfirmTimer) clearTimeout(fallbackConfirmTimer)
  watchdogTimer = null
  fallbackTimer = null
  fallbackConfirmTimer = null
  if (fallbackMode && userId) {
    postTabMessage({ type: 'leader_withdrawn', user_id: userId, tab_id: TAB_ID })
    if (clearSuspendedUser) clearFallbackLease(userId)
    else clearOwnFallbackLease(userId)
  }
  electionUnsubscribe?.()
  electionUnsubscribe = null
  fallbackCandidates.clear()
  fallbackMode = false
  leadershipExclusive = false
  started = false
  activeLockName = null
  activeUserId = null
  electionStartedAt = 0
  lastLeaderHeartbeatAt = 0
  if (clearSuspendedUser) suspendedUserId = null
  setLeader(false)
}

export function isLeaderTab(): boolean {
  return isLeader
}

/** Можно ли безопасно снимать claims прежнего лидера. */
export function hasExclusiveLeadership(): boolean {
  return isLeader && leadershipExclusive
}

export function onLeadershipChange(listener: LeadershipListener): () => void {
  listeners.add(listener)
  listener(isLeader)
  return () => listeners.delete(listener)
}

/* ===================================================================== */
/* Канал между вкладками                                                  */
/* ===================================================================== */

export type TabMessage =
  | { type: 'auth_transition_started'; transition_id: string; source_tab_id: string }
  | { type: 'auth_transition_complete'; transition_id: string; source_tab_id: string }
  | { type: 'logout'; user_id: string }
  | { type: 'session_lost'; user_id: string }
  | { type: 'owner_changed'; previous_user_id: string; next_user_id: string }
  | { type: 'force_reload'; user_id: string }
  | { type: 'replica_changed' }
  | {
      type: 'leader_candidate'
      user_id: string
      tab_id: string
      started_at: number
      sent_at: number
    }
  | {
      type: 'leader_heartbeat'
      user_id: string
      tab_id: string
      started_at: number
      sent_at: number
    }
  | { type: 'leader_ready'; user_id: string; tab_id: string }
  | { type: 'leader_withdrawn'; user_id: string; tab_id: string }
  | {
      type: 'sync_status'
      user_id: string
      status:
        | 'idle'
        | 'initial_snapshot'
        | 'replaying'
        | 'live'
        | 'polling_fallback'
        | 'reset_required'
        | 'offline'
    }
  | {
      type: 'media_event'
      user_id: string
      media_id: string
      state: 'ready' | 'rejected'
      rejected_reason?: string
    }
  | {
      type: 'sync_effects'
      user_id: string
      incoming_message?: Message
      match_created?: Match
    }
  | {
      type: 'sync_command'
      user_id: string
      source_tab_id: string
      command:
        | { kind: 'typing'; conversation_id: string; state: 'start' | 'stop' }
        | {
            kind: 'receipt'
            conversation_id: string
            delivered_seq?: number
            read_seq?: number
          }
        | { kind: 'active_chat'; conversation_id: string | null }
        | { kind: 'visible'; conversation_ids: string[] }
    }

const CHANNEL_NAME = 'dvizh-app'

let channel: BroadcastChannel | null = null
let channelUnavailable = false

function getChannel(): BroadcastChannel | null {
  if (channelUnavailable || typeof BroadcastChannel === 'undefined') return null
  try {
    channel ??= new BroadcastChannel(CHANNEL_NAME)
  } catch {
    // Privacy policy может оставить constructor в globalThis,
    // но запретить его вызов. Дальше работаем как без BC.
    channelUnavailable = true
    channel = null
  }
  return channel
}

export function postTabMessage(message: TabMessage): boolean {
  const bc = getChannel()
  if (!bc) return false
  try {
    bc.postMessage(message)
    return true
  } catch {
    return false
  }
}

export function onTabMessage(listener: (message: TabMessage) => void): () => void {
  const bc = getChannel()
  if (!bc) return () => undefined

  const handler = (event: MessageEvent): void => listener(event.data as TabMessage)
  try {
    bc.addEventListener('message', handler)
    return () => bc.removeEventListener('message', handler)
  } catch {
    return () => undefined
  }
}
